<template>
  <div id="room" class="main_liv overflow-hidden position-absolute">
    <!-- GOOGLE ANALYTICS -->
    <GAnalytics
      v-if="tourFeatures.analytics.enabled && tourFeatures.analytics.id"
      :stream="tourFeatures.analytics.id"
      :userId="loggedUserId"
    />

    <!-- VIS LOGO FULL PAGE (no specified tour to show) -->
    <div v-if="virtualTourData && virtualTourData.error" id="home" class="bg-dark w-100 h-100">
      <div class="row h-100 justify-content-center align-items-center">
        <div class="col text-center">
          <img :src="logo" alt="Virtual Iteractive Showroom" class="w-50" />
        </div>
      </div>
    </div>
    <!-- HEADER -->
    <b-navbar sticky class="header-nav">
      <a v-if="virtualTourData.attributes.logo.link" :href="virtualTourData.attributes.logo.link" target="_blank"
        ><img :src="logo" class="m-0 p-0" height="30px"
      /></a>
      <img v-if="!virtualTourData.attributes.logo.link" :src="logo" class="m-0 p-0" height="30px" />

      <b-navbar-nav class="ml-auto">
        <div class="header-icon" v-if="!appIsFetching && !error && virtualTourData && !virtualTourData.error">
          <b-button v-if="tourFeatures.wishlist.enabled" v-b-modal.wishlist-popup class="mt-0 p-0 mx-2">
            <font-awesome-icon :icon="['far', 'heart']" /><b-badge class="border mx-n2 text-center">
              {{ wishlistItemsNumber(tour) }}</b-badge
            >
          </b-button>

          <SwitchLanguage
            v-if="
              Array.isArray(virtualTourData.attributes.lang.available) &&
              virtualTourData.attributes.lang.available.length > 1
            "
            :langs="virtualTourData.attributes.lang.available"
          />

          <button class="btn" @click="logout()" v-if="isAuthenticated">
            <font-awesome-icon icon="sign-out-alt" class="align-middle" />
          </button>
        </div>
      </b-navbar-nav>
    </b-navbar>

    <div v-if="!appIsFetching && !error && virtualTourData && !virtualTourData.error" class="h-100">
      <IntroPopup />
      <PopupFactory
        ref="popup"
        :socket="socket"
        :buttonCloseClick="onPopupButtonCloseClick"
        :startChat="onStartChat"
        :enterRoom="onRoomEnter"
      />
      <Wishlist v-if="tourFeatures.wishlist.enabled" />

      <div v-if="!socket.haveControl" class="viewer-overlay">
        <h3 class="text-danger position-absolute m-3 d-none d-md-inline">
          &bull; {{ $t('COBROWSING_OPERATOR_CONTROL_MESSAGE') }}
        </h3>
        <h6 class="text-danger position-absolute m-3 d-md-none">
          &bull; {{ $t('COBROWSING_OPERATOR_CONTROL_MESSAGE') }}
        </h6>
      </div>

      <KrpanoViewer
        ref="viewer"
        v-if="xmlString && !error"
        :xml="xmlString"
        :canMove="socket.haveControl && !isTutorialVisible"
        @popupHotspotClick="onPopupHotspotClick"
        @tourMapClick="onTourMapClick"
        @streamViewSend="onStreamViewSend"
        @streamPosSend="onStreamPosSend"
        @videoClick="onVideoClick"
      />

      <CometChat
        v-if="tourFeatures.chat.enabled && chatActive"
        ref="chat"
        @startCobrowsing="onStartCobrowsing"
        @stopCobrowsing="onStopCobrowsing"
        @logout="logout"
      />
      <!-- Map popup -->
      <TourMap
        v-if="tourFeatures.map.enabled || tourFeatures.interactiveMap.enabled"
        :params="tourFeatures.map"
        :showIcon="showMapIcon"
        :desks="deskForMap"
        @goToDesk="onRoomEnter"
        @rotateToDesk="onStreamViewReceive"
      />

      <!-- Interface button where the interactive map can be shown -->
      <button
        id="tour-tutorial-button"
        class="btn btn-dark"
        :class="[showMapIcon ? 'with-map' : tourFeatures.interactiveMap.enabled ? 'with-interactive-map' : '']"
        @click="sendTracking({ action: 'into_popup', category: 'ui_buttons', label: 'opened' })"
        v-b-modal.intro-popup
      >
        <font-awesome-icon class="h-100 w-75" :icon="['fas', 'question']" />
      </button>

      <!-- Reply branding -->
      <div v-if="virtualTourData.attributes.branding.enabled">
        <a target="_blank" :href="virtualTourData.attributes.branding.link"
          ><img id="branding-button" role="button" :src="brandingImagePath" />
        </a>
      </div>
    </div>

    <div v-if="error && !appIsFetching" class="h-100 text-danger d-flex justify-content-center align-items-center">
      <span v-if="error.response && error.response.data && error.response.data.data">{{
        error.response.data.data
      }}</span>
      <span v-else>{{ error }}</span>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import CobrowsingSocket from '@/services/CobrowsingSocket'
import logoDefault from '@/assets/virtual-store-logo-full.svg'

const log = require('bows')('Room.vue', '[FE]')

export default {
  name: 'Room',
  components: {
    GAnalytics: () => import('@/components/GAnalytics'),
    SwitchLanguage: () => import('@/components/SwitchLanguage'),
    KrpanoViewer: () => import('@/components/KrpanoViewer'),
    PopupFactory: () => import('@/components/PopupFactory'),
    IntroPopup: () => import('@/components/IntroPopup'),
    Wishlist: () => import('@/components/Wishlist'),
    CometChat: () => import('@/components/CometChat'),
    TourMap: () => import('@/components/TourMap')
  },
  data: () => ({
    socket: null,
    userUrl: null,
    shareLnkActive: false,
    canMove: true,
    lastViewpoint: null,
    lastLookat: null,
    logoDefault
  }),
  computed: {
    logo() {
      let logoPath = logoDefault
      if (this.virtualTourData && this.virtualTourData.attributes && this.virtualTourData.attributes.logo.image) {
        logoPath = `/contents/${process.env.VUE_APP_TOUR}/${this.virtualTourData.attributes.logo.image}`
      }
      return logoPath
    },
    ...mapGetters([
      'virtualTourData',
      'deskForMap',
      'error',
      'tour',
      'isTutorialVisible',
      'wishlistItemsNumber',
      'appIsFetching',
      'xmlString',
      'chatActive',
      'isAuthenticated',
      'loggedUserId',
      'loggedUsername',
      'tourFeatures',
      'isMobileDevice'
    ]),
    wishlistItemsTotal() {
      return this.wishlistItemsNumber(this.tour)
    },
    showMapIcon() {
      return this.tourFeatures.map.enabled || (this.tourFeatures.interactiveMap.enabled && this.isMobileDevice)
    },
    brandingImagePath() {
      return `/contents/${process.env.VUE_APP_TOUR}/${this.virtualTourData.attributes.branding.logo}`
    }
  },
  created() {
    // Create socket and assign callback
    this.socket = new CobrowsingSocket()
    this.socket.forceview = this.onStreamViewReceive
    this.socket.goto = this.onStreamPosReceive
    this.socket.showpopup = this.onPopupHotspotClick
    this.socket.showmap = this.onTourMapClick
    this.socket.closepopup = this.onPopupButtonCloseClick
    this.socket.streamopen = this.onOpenCobrowsing

    this.lastViewpoint = this.virtualTourData.attributes.scene.startingViewPoint
    this.lastLookat = {
      hlookat: this.virtualTourData.attributes.scene.startingHlookat
        ? this.virtualTourData.attributes.scene.startingHlookat
        : 90,
      vlookat: 0
    }
    this.createXML()
    if (this.isAuthenticated) {
      this.setChatActive()
    }

    this.$bvModal.show('tour-map')
  },
  methods: {
    ...mapActions({
      setPopupData: 'SET_POPUP_DATA',
      fetchPopupData: 'FETCH_POPUP_DATA',
      setPopupId: 'SET_POPUP_ID',
      setPopupVisible: 'SET_IS_POPUP_VISIBLE',
      setTutorialVisible: 'SET_IS_TUTORIAL_VISIBLE',
      fetchRoomData: 'ROOM_GET_CONNECTION_DATA',
      setChatActive: 'CHAT_SET_ACTIVE',
      createXML: 'KRPANO_XML_STRING',
      sendTracking: 'TRACKER_SEND_EVENT',
      chatLogout: 'CHAT_LOGOUT',
      authLogout: 'AUTH_LOGOUT'
    }),
    reload() {
      window.location.reload()
    },
    logout() {
      this.sendTracking({ action: 'logout', category: 'ui_buttons' })
      // if chat enabled logout from chat
      const cometChatEnabled =
        this.virtualTourData.attributes &&
        this.virtualTourData.attributes.chat &&
        this.virtualTourData.attributes.chat.enabled
      // if auth enabled logout from auth
      const authenticationEnabled =
        this.virtualTourData.attributes &&
        this.virtualTourData.attributes.authentication &&
        this.virtualTourData.attributes.authentication.enabled
      const firstStep = cometChatEnabled ? this.chatLogout(this.tour) : new Promise(resolve => resolve())
      const secondStep = authenticationEnabled ? this.authLogout() : new Promise(resolve => resolve())
      firstStep
        .then(() => {
          secondStep.then(() => {
            this.$store.dispatch('APP_IS_FETCHING')
            this.reload()
          })
        })
        .catch(err => {
          log(err)
          secondStep
            .then(() => {
              this.$store.dispatch('APP_IS_FETCHING')
              this.reload()
            })
            .catch(err => {
              log(err)
              this.$store.dispatch('APP_IS_FETCHING')
              this.reload()
            })
        })
    },
    closePopup() {
      this.setTutorialVisible(false)
    },
    onPopupButtonCloseClick() {
      this.$bvModal.hide('popup-modal')
      if (this.socket.socketConn) {
        this.socket.onPopupButtonCloseSend()
      }
    },
    onPopupHotspotClick(popupId) {
      log(`Tag clicked: ${popupId}`)
      this.sendTracking({ action: 'tag_clicked', category: 'user_interactions', label: popupId })
      if (this.socket && this.socket.socketConn) {
        this.socket.popupHotspotSend(popupId)
      }
      this.fetchPopupData(popupId).then(data => {
        if (data && data.action === 'popup' && this.$refs.popup) {
          this.$refs.popup.showPopup()
        } else if (data && data.action === 'external') {
          const link = document.createElement('a')
          link.target = '_blank'
          link.href = data.url
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        } else if (data && data.action === 'map') {
          this.$bvModal.show('tour-map')
        }
      })
    },
    onTourMapClick() {
      this.$bvModal.show('tour-map')
      this.sendTracking({ action: 'map_open', category: 'ui_buttons' })
    },
    onVideoClick(url) {
      const iframeData = {
        title: '',
        url: url,
        type: 'iframe'
      }
      this.setPopupData(iframeData).then(() => {
        this.$refs.popup.showPopup()
      })
    },
    onStreamViewSend(data) {
      this.lastLookat = data
      if (this.socket && this.socket.socketConn) {
        this.socket.onStreamViewSend(data)
      }
    },
    onStreamPosSend(data) {
      this.lastViewpoint = data
      if (this.socket && this.socket.socketConn) {
        this.socket.onStreamPosSend(data)
      }
    },
    onRoomEnter(data) {
      this.lastViewpoint = data
      if (this.$refs.viewer) this.$refs.viewer.goto(data)
      if (this.socket && this.socket.socketConn) {
        this.socket.onStreamPosSend(data)
      }
    },
    onStreamViewReceive(data) {
      if (this.$refs.viewer) this.$refs.viewer.forceview(data)
    },

    onStreamPosReceive(data) {
      if (this.$refs.viewer) this.$refs.viewer.goto(data)
    },
    onStartChat(uid) {
      if (this.$refs.chat) this.$refs.chat.launchChat(uid)
    },
    onStartCobrowsing(cid) {
      if (this.virtualTourData.attributes.chat.cobrowsing) {
        if (this.socketConn) {
          this.socket.end()
        }
        this.fetchRoomData({ cid, tourSlug: this.tour }).then(data => {
          this.socket.connect(data)
          this.socket.isMaster = false
          this.socket.haveControl = true
        })
      }
    },
    onOpenCobrowsing() {
      setTimeout(() => {
        log('sending starting view', this.lastViewpoint)
        this.socket.onStreamPosSend(this.lastViewpoint)
        this.socket.onStreamViewSend(this.lastLookat)
      }, 1000)
    },
    onStopCobrowsing() {
      this.socket.haveControl = true
      this.socket.end()
    }
  }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;700&display=swap');
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');
@import '../assets/css/mixins.scss';

$mainColor: #28333c;
$navbar-height: 50px;
$default-border-radius: 0.5rem;

.viewer-overlay {
  z-index: 16;
  border: 5px solid red;
  position: absolute;
  width: calc(100vw);
  height: calc(100vh - 50px);
  top: 50px;
  left: 0;
}

#tour-tutorial-button {
  position: absolute;
  border: none;
  bottom: 2vh;
  left: 2vw;
  width: 50px;
  height: 50px;
  border-radius: 25px;
  z-index: 15;

  &.with-map {
    bottom: calc(2vh + 110px);
  }

  &.with-interactive-map {
    bottom: calc(2vh + 250px);
  }
}

#branding-button {
  position: absolute;
  right: 2vw;
  bottom: 2vh;
  height: 32px;
  width: auto;
}

.header-nav {
  height: $navbar-height;
  margin-bottom: -$navbar-height;
  background-color: #000 !important;
  z-index: 100;

  .badge {
    display: inline-flex;
    font-size: 0.55rem;
    color: black;
    border-radius: 50%;
    width: 1.25rem;
    height: 1.2rem;
    align-items: center;
    justify-content: center;
    background-color: white;
    border-color: #000 !important;
  }

  .header-icon {
    margin-top: -0.5rem;
    margin-bottom: -0.5rem;
  }

  .btn {
    color: white !important;
    background-color: black !important;
    border: none;
    font-size: 1.4rem;
  }
}

#urlRoom {
  padding: 30px;
  input {
    @include Opacity(0);
  }
}

#room {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
}

*:focus,
* {
  outline-width: 0;
}
* {
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  -ms-box-sizing: border-box;
  box-sizing: border-box;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s;
}
.fade-enter,
.fade-leave-to {
  margin-top: 250%;
}
</style>
